import Vue from 'vue';
import VueI18n from '@/libs/i18n/index';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import systemServerErrorHelper from './system-server-error-helper';
import { SERVER_AI_ERROR_CODE } from '../enum/enum-server-ai-error-code';

const position = "bottom-right";
const timeout = 4000;

class MessagesNotifyHelper {

    createdRecordNotify() {
        this.successNotify(VueI18n.t('GENERAL').Create, VueI18n.t('MESSAGES').Messages_Created);
    }

    updatedRecordNotify() {
        this.successNotify(VueI18n.t('GENERAL').Update, VueI18n.t('MESSAGES').Messages_Updated);
    }

    deletedRecordNotify() {
        this.successNotify(VueI18n.t('GENERAL').Delete, VueI18n.t('MESSAGES').Messages_Deleted);
    }

    genericErrorNotify() {
        this.errorNotify(VueI18n.t('GENERAL').Error, VueI18n.t('MESSAGES').Messages_Generic_Error);
    }

    /**
     * Texto traduzido (i18n) de um erro do backend identificado por errorType + code.
     * Retorna '' quando o erro não é um erro codificado do backend ou não há tradução.
     */
    getErrorText(error) {
        if (!error || error instanceof Error) return '';
        if (error.errorType == 'system-server') {
            return systemServerErrorHelper.getTranslateSystemServerErrorText(error.code) || '';
        }
        if (error.errorType == 'ai-error') {
            return systemServerErrorHelper.getTranslateServerAiError(error.code) || '';
        }
        return '';
    }

    /**
     * O interceptor do axios ja exibe o toast dos erros de resposta do backend e rejeita o
     * response.data (objeto simples). Um Error de verdade (validacao local, rede) nao foi notificado.
     */
    isAlreadyNotified(error) {
        return !!error && !(error instanceof Error);
    }

    /** Erro de IA causado por falta de contexto (campos vazios ou a IA respondeu que nao ha conteudo suficiente). */
    isAiContextError(error) {
        if (!error || error.errorType != 'ai-error') return false;
        return [SERVER_AI_ERROR_CODE.AI_CONTEXT_REQUIRED.code, SERVER_AI_ERROR_CODE.AI_INSUFFICIENT_CONTEXT.code].includes(error.code);
    }

    /** Notifica o erro apenas se o interceptor ainda nao o fez, usando o texto traduzido do codigo quando existir. */
    errorNotifyUnlessHandled(title, error, fallbackText) {
        if (this.isAlreadyNotified(error)) return;
        const localMessage = error && !error.isAxiosError && error.message;
        this.errorNotify(title, this.getErrorText(error) || localMessage || fallbackText);
    }

    sessionExpiredNotify() {
        this.warningNotify(VueI18n.t('MESSAGES.Session_Expired'), VueI18n.t('MESSAGES.Session_Expired_Login_Again'));
    }

    serverUnavailableNotify() {
        this.warningNotify('503', VueI18n.t('SYSTEM_SERVER_ERROR.internalServerErrorMessage'));
    }

    fileTooLargeNotify() {
        this.errorNotify('413', VueI18n.t('MESSAGES.File_Too_Large'));
    }

    operationErrorNotify(title) {
        this.errorNotify(title || VueI18n.t('GENERAL.Error'), VueI18n.t('MESSAGES.Messages_Generic_Error'));
    }

    handleError(error) {
        if (error.errorType == 'system-server') {
            const text = systemServerErrorHelper.getTranslateSystemServerErrorText(error.code);
            if (text) {
                this.errorNotify(VueI18n.t('DIALOG_MESSAGES').Title, text);
                return;
            }
            this.genericErrorNotify();
            return;
        }
        if (error.errorType == 'ai-error') {
            const text = systemServerErrorHelper.getTranslateServerAiError(error.code);
            // Falta de contexto nao e falha da IA: orienta o usuario com um aviso, nao com erro
            if (text && this.isAiContextError(error)) {
                this.warningNotify(VueI18n.t('AI_MESSAGES.ContextRequiredTitle'), text);
                return;
            }
            if (text) {
                this.errorNotify(VueI18n.t('DIALOG_MESSAGES').Title, text);
                return;
            }
            this.genericErrorNotify();
            return;
        }
        if (Array.isArray(error)) {
            this.errorNotify(VueI18n.t('GENERAL.Error'), VueI18n.t('MESSAGES.Multiple_Errors'));
            return;
        }

        if (error.erros) {
            this.errorNotify(error.erros[0].field, error.erros[0].message);
            return;
        }
        if (error.message) {
            if (error.message.indexOf('Access Denied') >= 0) {
                this.warningNotify(VueI18n.t('MESSAGES.Access_Title'), VueI18n.t('MESSAGES.Access_Denied'));
                return;
            } else if (Array.isArray(error.message)) {
                error.message.forEach(item => {
                    if (item.includes('content.title.')) {
                        item = item.replace('content.title.','');
                    }
                    this.errorNotify(VueI18n.t('GENERAL.Error'), item);
                });
                return;
            } else {
                this.errorNotify(VueI18n.t('GENERAL.Error'), error.message);
            }
            return;
        }

        this.operationErrorNotify();
    }

    handleErrorSendNews(error) {
        for (let index = 0; index < error.length; index++) {
            this.errorNotify(VueI18n.t('GENERAL.Error'), error[index].message);
        }
    }

    errorNotify(title, text) {
        const variant = "danger";
        
        Vue.prototype.$toast({
            component: ToastificationContent,
            props: {
                title: title,
                icon: "",
                text: text,
                variant
            },
        }, { position, timeout });
    }

    successNotify(title, text) {
        const variant = "success";
        Vue.prototype.$toast({
            component: ToastificationContent,
            props: {
                title: title,
                icon: "",
                text: text,
                variant
            },
        }, { position, timeout });
    }

    warningNotify(title, text) {
        const variant = "warning";
        Vue.prototype.$toast({
            component: ToastificationContent,
            props: {
                title: title,
                icon: "",
                text: text,
                variant
            },
        }, { position, timeout });
    }

    messageNotify(title, text) {
        const variant = "dark";
        Vue.prototype.$toast({
            component: ToastificationContent,
            props: {
                title: title,
                icon: "",
                text: text,
                variant
            },
        }, { position, timeout });
    }

}

export default new MessagesNotifyHelper();